import { Tile } from './Tiles';

export const isHonor = (t: Tile) => t.type === 'wind' || t.type === 'dragon';
export const isTerminal = (t: Tile) => typeof t.value === 'number' && (t.value === 1 || t.value === 9);
export const isTerminalOrHonor = (t: Tile) => isHonor(t) || isTerminal(t);
export const isSimple = (t: Tile) => !isTerminalOrHonor(t);

export const getCounts = (hand: Tile[]) => {
    const counts: Record<string, number> = {};
    for (const t of hand) {
        const key = `${t.type}_${t.value}`;
        counts[key] = (counts[key] || 0) + 1;
    }
    return counts;
};

// Basic hand decomposition (Standard 4 sets + 1 pair)
// This is a simplified checker that tries to find *any* valid decomposition.
export interface Decomposition {
    pair: Tile[];
    sets: Tile[][]; // Array of sets (each set is array of Tiles)
}

export function decomposeHand(hand: Tile[]): Decomposition[] {
    // Sort hand for easier processing
    const sorted = [...hand].sort((a, b) => {
        if (a.type !== b.type) return a.type.localeCompare(b.type);
        if (typeof a.value === 'number' && typeof b.value === 'number') return a.value - b.value;
        return String(a.value).localeCompare(String(b.value));
    });

    const results: Decomposition[] = [];
    const counts = getCounts(sorted);
    const uniqueTiles = Array.from(new Set(sorted.map(t => `${t.type}_${t.value}`)));

    // Try every tile as a pair
    for (const pairKey of uniqueTiles) {
        if (counts[pairKey] >= 2) {
            const remainingCounts = { ...counts };
            remainingCounts[pairKey] -= 2;

            const sets: Tile[][] = [];
            if (findSets(remainingCounts, sets)) {
                // Reconstruct pair tiles
                const pairTile = sorted.find(t => `${t.type}_${t.value}` === pairKey)!;
                results.push({
                    pair: [pairTile, pairTile],
                    sets: sets
                });
            }
        }
    }
    return results;
}

function findSets(counts: Record<string, number>, sets: Tile[][]): boolean {
    // Find first tile with count > 0
    let firstKey: string | null = null;
    for (const key in counts) {
        if (counts[key] > 0) {
            firstKey = key;
            break;
        }
    }

    if (!firstKey) return true; // No tiles left, success

    const [type, valStr] = firstKey.split('_');
    const val = isNaN(Number(valStr)) ? valStr : Number(valStr);

    // Try Triplet
    if (counts[firstKey] >= 3) {
        counts[firstKey] -= 3;
        sets.push([
            { type: type as any, value: val as any, id: '', isRed: false },
            { type: type as any, value: val as any, id: '', isRed: false },
            { type: type as any, value: val as any, id: '', isRed: false }
        ]);
        if (findSets(counts, sets)) return true;
        sets.pop();
        counts[firstKey] += 3;
    }

    // Try Sequence (only for numbers)
    if (typeof val === 'number' && type !== 'wind' && type !== 'dragon') {
        const k1 = `${type}_${val}`;
        const k2 = `${type}_${val + 1}`;
        const k3 = `${type}_${val + 2}`;

        if (counts[k1] > 0 && counts[k2] > 0 && counts[k3] > 0) {
            counts[k1]--;
            counts[k2]--;
            counts[k3]--;
            sets.push([
                { type: type as any, value: val, id: '', isRed: false },
                { type: type as any, value: val + 1, id: '', isRed: false },
                { type: type as any, value: val + 2, id: '', isRed: false }
            ]);
            if (findSets(counts, sets)) return true;
            sets.pop();
            counts[k1]++;
            counts[k2]++;
            counts[k3]++;
        }
    }

    return false;
}
